<template>
  <div class="cyber-loading" :class="{ 'fade-out': !isLoading }">
    <div class="stars"></div>
    <div class="stars2"></div>
    <div class="stars3"></div>
    
    <div class="loading-container">
      <div class="blocks-container">
        <div v-for="i in 3" :key="i" class="block" :class="'block-' + i">
          <v-icon color="#76c4ae" size="40">mdi-space-invaders</v-icon>
          <div class="laser-hit" :class="'laser-hit-' + i"></div>
        </div>
      </div>
      <div class="lasers" :class="{ 'animate-lasers': animateSpaceship }">
        <div class="laser laser-1"></div>
        <div class="laser laser-2"></div>
        <div class="laser laser-3"></div>
      </div>
      <img 
        src="@/assets/logos/brand_icon_element_color_webp.webp" 
        alt="ALTERLAN" 
        class="spaceship"
        :class="{ 'animate-spaceship': animateSpaceship }"
      />
      
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'

const props = defineProps<{
  isLoading: boolean
}>()

const animateSpaceship = ref(false)


onMounted(() => {
  animateSpaceship.value = true


  // Clean up after animation
  setTimeout(() => {
    props.isLoading = false
  }, 1500)
})


</script>

<style scoped>
.cyber-loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #414a4a;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  overflow: hidden;
  transition: opacity 0.5s ease-out;
}

.cyber-loading.fade-out {
  opacity: 0;
  pointer-events: none;
}

.loading-container {
  position: relative;
  width: 100%;
  height: 100vh;
  min-height: 400px;
  overflow: hidden;
}

.blocks-container {
  position: absolute;
  display: flex;
  gap: 2rem;
  top: 25%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
}

.block {
  width: 40px;
  height: 40px;
  background: transparent;
  position: relative;
  opacity: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.block-1 { 
  transform: translateX(-40px);
}

.block-2 {
  transform: translateY(0);
}

.block-3 {
  transform: translateX(40px);
}

.block::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #76c4ae;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.block.hit::before {
  opacity: 1;
}

.cyber-text {
  position: absolute;
  bottom: 10%;
  left: 50%;
  transform: translateX(-50%);
  color: #76c4ae;
  font-size: 1rem;
  text-align: center;
}

.stars, .stars2, .stars3 {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
}

.stars::before, .stars2::before, .stars3::before,
.stars::after, .stars2::after, .stars3::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-image: 
    radial-gradient(1px, #76c4ae88 1px, transparent 1px),
    radial-gradient(1px, #76c4ae66 1px, transparent 1px);
  background-position: 
    calc(10% + 15px) calc(20% + 25px),
    calc(85% - 10px) calc(35% - 15px),
    calc(25% + 20px) calc(65% + 5px),
    calc(70% - 5px) calc(80% - 20px),
    calc(45% + 25px) calc(15% + 10px),
    calc(90% - 15px) calc(55% - 5px),
    calc(15% + 5px) calc(75% + 15px),
    calc(60% - 20px) calc(40% - 10px);
  background-size: 200px 200px;
  background-repeat: repeat;
  animation: animStar 3s linear infinite;
}

.stars::after, .stars2::after, .stars3::after {
  top: -100%;
}

.stars2::before, .stars2::after {
  background-image: 
    radial-gradient(1.5px, #76c4ae88 1.5px, transparent 1.5px),
    radial-gradient(1.5px, #76c4ae66 1.5px, transparent 1.5px);
  background-position: 
    calc(30% + 10px) calc(45% + 15px),
    calc(75% - 20px) calc(25% - 5px),
    calc(15% + 25px) calc(85% + 20px),
    calc(60% - 15px) calc(50% - 15px),
    calc(40% + 5px) calc(30% + 25px),
    calc(95% - 25px) calc(70% - 20px),
    calc(20% + 15px) calc(60% + 10px),
    calc(80% - 10px) calc(15% - 25px);
  background-size: 300px 300px;
  animation: animStar 4s linear infinite;
  animation-delay: -1s;
}

.stars3::before, .stars3::after {
  background-image: 
    radial-gradient(2px, #76c4ae88 2px, transparent 2px),
    radial-gradient(2px, #76c4ae66 2px, transparent 2px);
  background-position: 
    calc(5% + 20px) calc(55% + 10px),
    calc(65% - 15px) calc(40% - 20px),
    calc(35% + 5px) calc(90% + 15px),
    calc(85% - 25px) calc(20% - 10px),
    calc(50% + 15px) calc(75% + 5px),
    calc(25% - 5px) calc(35% - 15px),
    calc(70% + 25px) calc(60% + 20px),
    calc(45% - 20px) calc(95% - 5px);
  background-size: 400px 400px;
  animation: animStar 5s linear infinite;
  animation-delay: -2s;
}

@keyframes animStar {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
}

@keyframes spaceshipAttack {
  0% {
    transform: translate(-50%, -50%) scale(0.8);
    opacity: 0;
  }
  20% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  70% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -300%) scale(1.2);
    opacity: 0;
  }
}

.spaceship {
  position: absolute;
  top: 75%;
  left: 50%;
  width: 80px;
  height: 80px;
  transform: translate(-50%, -50%);
  opacity: 0;
  z-index: 3;
  animation: spaceshipAttack 1.3s ease-in-out forwards;
}

.lasers {
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 2;
}

.laser {
  position: absolute;
  height: 4px;
  width: 0;
  background: linear-gradient(to right, #76c4ae 0%, #76c4ae 70%, rgba(0, 0, 0, 0.8) 100%);
  transform-origin: left;
  box-shadow: 
    0 0 5px #76c4ae,
    0 0 10px #76c4ae,
    0 0 20px #76c4ae;
}

.laser-1 {
  top: 73%;
  left: 50%;
}

.laser-2 {
  top: 73%;
  left: 50%;
}

.laser-3 {
  top: 73%;
  left: 50%;
}

.animate-lasers .laser-1 {
  animation: 
    shootLaser1 0.4s cubic-bezier(0.5, 0, 0.5, 1) forwards 0.2s;
}

.animate-lasers .laser-2 {
  animation: 
    shootLaser2 0.4s cubic-bezier(0.5, 0, 0.5, 1) forwards 0.3s;
}

.animate-lasers .laser-3 {
  animation: 
    shootLaser3 0.4s cubic-bezier(0.5, 0, 0.5, 1) forwards 0.4s;
}

@keyframes shootLaser1 {
  0% {
    width: 0;
    opacity: 0;
    transform: rotate(-101deg);
  }
  15% {
    width: 100px;
    opacity: 1;
    transform: rotate(-101deg) ;
  }
  100% {
    width: calc(100vh * 0.5);
    opacity: 0;
    transform: rotate(-101deg);
  }
}

@keyframes shootLaser2 {
  0% {
    width: 0;
    opacity: 0;
    transform: rotate(-90deg) ;
  }
  15% {
    width: 100px;
    opacity: 1;
    transform: rotate(-90deg);
  }
  100% {
    width: calc(100vh * 0.5);
    opacity: 0;
    transform: rotate(-90deg);
  }
}

@keyframes shootLaser3 {
  0% {
    width: 0;
    opacity: 0;
    transform: rotate(-79deg);
  }
  15% {
    width: 10px;
    opacity: 1;
    transform: rotate(-79deg);
  }
  100% {
    width: calc(100vh * 0.5);
    opacity: 0;
    transform: rotate(-79deg);
  }
}

.laser-hit {
  position: absolute;
  border-radius: 50%;
  width: 100%;
  height: 100%;
  opacity: 0;
  background: radial-gradient(circle at center, #76c4ae 0%, transparent 70%);
  box-shadow: 
    0 0 10px #76c4ae,
    0 0 20px #76c4ae,
    0 0 30px #76c4ae;
}

.block-1 .laser-hit { animation: laserHit 0.3s ease-out forwards 0.5s; }
.block-2 .laser-hit { animation: laserHit 0.3s ease-out forwards 0.6s; }
.block-3 .laser-hit { animation: laserHit 0.3s ease-out forwards 0.7s; }

.block-1 { 
  animation: blockBreak1 0.5s ease-in-out forwards 0.6s;
}

.block-2 {
  animation: blockBreak2 0.6s ease-in-out forwards 0.7s;
}

.block-3 {
  animation: blockBreak3 0.7s ease-in-out forwards 0.8s;
}

@keyframes laserHit {
  0% {
    opacity: 0;
    transform: scale(0.5);
    filter: brightness(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.5);
    filter: brightness(3);
  }
  100% {
    opacity: 0;
    transform: scale(2);
    filter: brightness(1);
  }
}

@keyframes blockBreak1 {
  0% { 
    transform: translateX(-40px) rotate(0); 
    opacity: 1;
  }
  10% { 
    transform: translateX(-45px) rotate(-5deg);
    opacity: 1;
  }
  100% { 
    transform: translateX(-140px) rotate(-45deg);
    opacity: 0;
  }
}

@keyframes blockBreak2 {
  0% { 
    transform: translateY(0) rotate(0);
    opacity: 1;
  }
  10% { 
    transform: translateY(-5px) rotate(5deg);
    opacity: 1;
  }
  100% { 
    transform: translateY(-150px) rotate(45deg);
    opacity: 0;
  }
}

@keyframes blockBreak3 {
  0% { 
    transform: translateX(40px) rotate(0);
    opacity: 1;
  }
  10% { 
    transform: translateX(45px) rotate(5deg);
    opacity: 1;
  }
  100% { 
    transform: translateX(140px) rotate(45deg);
    opacity: 0;
  }
}
</style>
